import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  inject,
  input,
  signal,
  viewChild,
  OnInit,
  OnDestroy,
  Output,
  EventEmitter,
} from "@angular/core";
import { FormControl } from "@angular/forms";

import { TippyService } from "@ngneat/helipopper";
import { QuillEditorComponent } from "ngx-quill";

import { TextConstants } from "@shared/constants";

@Component({
  selector: "app-rich-text-editor",
  templateUrl: "./rich-text-editor.component.html",
  styleUrls: ["./rich-text-editor.component.scss"],
  changeDetection: ChangeDetectionStrategy.Default,
  standalone: false,
})
export class RichTextEditorComponent implements OnInit, OnDestroy {
  private readonly tippyService = inject(TippyService);

  private readonly editor = viewChild(QuillEditorComponent, { read: ElementRef });

  private readonly richTextContainer = viewChild<ElementRef, ElementRef>("richTextContainer", {
    read: ElementRef,
  });

  readonly control = input.required<FormControl>();

  readonly label = input<string>();

  readonly placeholder = input($localize`:@@shared.typecontenthere:Type your content here...`);

  readonly hint = input<string>();

  readonly isOptional = input(false);

  readonly enableViewMode = input(false);

  readonly showLabel = input(true);

  private styleElement: HTMLStyleElement;

  isExpanded = signal(false);

  hasMoreContent = signal(false);

  @Output() contentRendered = new EventEmitter<void>();

  readonly translations = {
    toolbar: [
      { selector: ".ql-bold", text: $localize`:@@format.bold:Bold` },
      { selector: ".ql-italic", text: $localize`:@@format.italic:Italic` },
      { selector: ".ql-underline", text: $localize`:@@format.underline:Underline` },
      {
        selector: ".ql-list[value='ordered']",
        text: $localize`:@@format.orderedlist:Ordered list`,
      },
      { selector: ".ql-list[value='bullet']", text: $localize`:@@format.bulletlist:Bullet list` },
      { selector: ".ql-link", text: $localize`:@@format.link:Link` },
      { selector: ".ql-clean", text: $localize`:@@format.clearformatting:Clear formatting` },
    ],
    linkTooltip: [
      {
        selector: ".ql-tooltip[data-mode='link']:before",
        text: $localize`:@@tooltip.enterlink:Enter link:`,
      },
      { selector: ".ql-tooltip[data-mode='link'] .ql-action:after", text: TextConstants.SAVE },
      { selector: ".ql-tooltip:before", text: $localize`:@@tooltip.visiturl:Visit URL:` },
      { selector: ".ql-tooltip .ql-action:after", text: TextConstants.EDIT },
      { selector: ".ql-tooltip .ql-remove:after", text: TextConstants.REMOVE },
    ],
    showMore: $localize`:@@shared.showmore:Show more...`,
    showLess: $localize`:@@shared.showless:Show less`,
  };

  ngOnInit() {
    this.replaceLinkTooltipselectors();
  }

  ngOnDestroy() {
    if (this.styleElement) {
      document.head.removeChild(this.styleElement);
    }
  }

  onQuillEditorCreated() {
    this.addToolbarTooltips();
  }

  onQuillViewCreated() {
    setTimeout(() => this.setHasMoreContent());
  }

  private addToolbarTooltips() {
    const editor = this.editor().nativeElement as Element;

    this.translations.toolbar.forEach(async (item) => {
      const element = editor.querySelector(`button${item.selector}`);

      if (element) {
        this.tippyService.create(element, item.text);
      }
    });
  }

  private replaceLinkTooltipselectors() {
    if (!this.styleElement) {
      this.styleElement = document.createElement("style");

      const styles = this.translations.linkTooltip
        .map((item) => `${item.selector} { content: "${item.text}" !important; }`)
        .join("\n");

      this.styleElement.appendChild(document.createTextNode(styles));
      document.head.appendChild(this.styleElement);
    }
  }

  private setHasMoreContent() {
    const element = this.richTextContainer()?.nativeElement as HTMLElement;

    if (!element) {
      return;
    }

    const fontSize = parseInt(window.getComputedStyle(element).fontSize);
    const lineHeight = fontSize * 1.2;

    const height = element.scrollHeight;

    this.hasMoreContent.set(height > lineHeight * 4);
  }
}
